import { useRef, useState } from 'react'
import { useAppSelector } from 'store/hooks'
import { selectedUser } from 'store/reducers/users'
import { PopupWrapper } from 'components/app/popup'
import {
  EMessageTypes,
  getVerifiedToken,
  resendVerificationCode,
  sendVerificationCodeWithoutCaptcha,
} from 'store/fetchers/utils'

import { GoogleAuthPopupProps } from 'components/ui/google-auth-popup'
import ButtonBlue from 'components/ui/button/_color/_blue'
import PopupCloseBtn from 'components/app/popup/partial/popup-close-btn'

import { createPortal } from 'react-dom'

import cn from 'classnames'

import PopupHeading from '../popup/components/__heading'

import PopupSubtitle from '../popup/components/__subtitle'

import ButtonWhite from '../../button/_color/_white'

import { goToChat } from '../../../../utils/go-to-chat'

import { fallbackRemove2fa, remove2fa } from '../../../../store/fetchers/auth'

import ArrowIcon from './assets/arrow.svg'
import SuccessIcon from './assets/success.svg'

import styles from './styles.module.scss'
import { useTranslation } from 'utils/use-translation'
import CodeVerificationInput from 'components/global/code-verification-input'

const TIMER_RESEND = 60

export enum ERemove2faFlow {
  AUTHENTICATOR = 'authenticator',
  BACKUP = 'backup',
}

export default function GoogleUnsetPopup({ onClose }: GoogleAuthPopupProps) {
  const { t } = useTranslation()

  const user = useAppSelector(selectedUser)

  const backupToken = useRef<string>(null)
  const verificationToken = useRef<string>(null)

  const [code, setCode] = useState<string>('')
  const [isCodeValid, setIsCodeValid] = useState(false)
  const [errorCode, setErrorCode] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1)

  const [removeFlow, setRemoveFlow] = useState<ERemove2faFlow>(null)

  const getEmailCode = async (isBackup: boolean): Promise<void> => {
    setStep(3)
    
    const { tokens } = await sendVerificationCodeWithoutCaptcha({
      ...{ email: user?.email, phone: user?.phone, },
      type: isBackup ? EMessageTypes.FALLBACK_REMOVE_2FA : EMessageTypes.TFA_DEACTIVATION,
    })

    verificationToken.current = tokens[0]
  }

  const getBackupEmailCode = async (): Promise<void> => {
    const { tokens } = await sendVerificationCodeWithoutCaptcha({
      ...{ email: user?.fallback_email },
      type: EMessageTypes.FALLBACK_REMOVE_2FA_BACKUP,
    })

    backupToken.current = tokens[0]
  }

  const verifyCode = async (verificationCode: string) => {
    setIsLoading(true)
    setErrorCode(null)

    try {
      if (step === 4) {
        if (removeFlow === ERemove2faFlow.AUTHENTICATOR) {
          send2faCode(verificationCode)
        } else {
          const res = await getVerifiedToken({
            code: verificationCode,
            verification_token: backupToken.current,
          })

          if (!res.result) {
            setIsCodeValid(null)
            setErrorCode(res.message || t('something_went_wrong'))
            setIsLoading(false)
            return
          }

          remove2faBackup(verificationCode)
        }
      } else {
        const res = await getVerifiedToken({
          code: verificationCode,
          verification_token: verificationToken.current,
        })

        if (!res.result) {
          setIsCodeValid(null)
          setErrorCode(res.message || t('something_went_wrong'))
          setIsLoading(false)
          return
        }

        if (removeFlow === ERemove2faFlow.BACKUP) {
          getBackupEmailCode()
        }

        setStep(4)
        setIsLoading(false)
      }
    } catch (err: any) {
      const msg = err?.response?.message || err?.response?.error || ''

      if (msg.includes('is invalid') || msg.includes('is not valid')) {
        setErrorCode(t('code_is_invalid'))
      } else if (msg.includes('limit exceeded') || msg.includes('try again')) {
        setErrorCode(t('rate_limit_exceeded_retry_later'))
      } else {
        setErrorCode(t('something_went_wrong'))
      }

      setIsLoading(false)
    }
  }

  const remove2faBackup = async (code: string) => {
    let res = null

    try {
      res = await fallbackRemove2fa({
        verification_token: [verificationToken.current, backupToken.current],
      })

      if (!res) {
        setIsCodeValid(false)
        setErrorCode(t('code_invalid_try_again_later'))
      } else {
        setStep(5)
      }
    } catch (err) {
      setIsCodeValid(false)
      setErrorCode(t('code_invalid_try_again_later'))
    } finally {
      setIsLoading(false)
    }
  }

  const send2faCode = async (code: string) => {
    let res = null

    try {
      res = await remove2fa({
        verification_token: verificationToken.current,
        code_2fa: code,
      })
      if (!res) {
        setIsCodeValid(false)
        setErrorCode(t('code_invalid_try_again_later'))
      } else {
        setStep(5)
      }
    } catch (err) {
      setIsCodeValid(false)
      setErrorCode(t('code_invalid_try_again_later'))
    } finally {
      setIsLoading(false)
    }
  }


  const resendEmailCode = async (captcha: string): Promise<void> => {
    const token = await resendVerificationCode({
      verification_token: verificationToken.current,
      captcha: captcha,
    })

    verificationToken.current = token
  }

  const resendBackupEmailCode = async (captcha: string): Promise<void> => {
    const token = await resendVerificationCode({
      verification_token: backupToken.current,
      captcha: captcha,
    })

    backupToken.current = token
  }

  const chooseAuthenticator = async () => {
    getEmailCode(false)
    setRemoveFlow(ERemove2faFlow.AUTHENTICATOR)
  }

  const chooseBackup = async () => {
    if (user.fallback_email) {
      getEmailCode(true)
      setRemoveFlow(ERemove2faFlow.BACKUP)
    }
  }

  return createPortal(
    <PopupWrapper onClose={onClose} classNames={[styles.container]}>
      <PopupCloseBtn onClose={onClose} className={styles['btn__close']} />

      {step === 1 && (
        <>
          <PopupHeading className={styles.title}>
            {t('want_to_remove_google_auth')}
          </PopupHeading>
          <div className={styles.popupDescription_space_top}>
            <ul className={styles.description}>
              <li className={styles.description__item}>
                {t('google_2fa_provides_security')}
              </li>
              <li className={styles.description__item}>
                {t('after_disabling_2fa_confirmation')}
              </li>
            </ul>
          </div>
          <div className={styles.lastRow}>
            <ButtonWhite className={styles.btnCancel} onClick={onClose}>{t('cancel')}</ButtonWhite>
            <ButtonBlue className={styles.btnNext} onClick={() => setStep(2)}>{t('confirm')}</ButtonBlue>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <PopupHeading className={cn(styles.title, styles['title-mobile'])}>
            {t('please_select_verification_option')}
          </PopupHeading>
          <ul className={styles.choose}>
            <li className={styles.choose__item} onClick={chooseAuthenticator}>
              <p>{t('google_authenticator')}</p>
              <img src={ArrowIcon} alt="arrow icon" />
            </li>
            <div className={styles.choose__line}></div>
            {user.fallback_email && (
              <>
                <li
                  className={cn(styles.choose__item, { [styles.disabled]: !user.fallback_email })}
                  onClick={chooseBackup}
                >
                  <p>{t('backup_email')}</p>
                  <img src={ArrowIcon} alt="arrow icon" />
                </li>
                <div className={styles.choose__line}></div>
              </>
            )}
            <li className={styles.choose__item} onClick={goToChat}>
              <p>
                {user.fallback_email
                  ? t('dont_have_access_to_both_write_to_support')
                  : t('dont_have_access_to_google_auth_write_support')}
              </p>
              <img src={ArrowIcon} alt="arrow icon" />
            </li>
          </ul>

          <ButtonWhite className={styles.backBtn} onClick={() => setStep(1)}>{t('back')}</ButtonWhite>
        </>
      )}

      {step === 3 && (
        <>
          <PopupHeading className={styles['title-verification']}>{t('remove_authenticator')}</PopupHeading>
          <PopupSubtitle className={styles.subtitle}>{t('verify_google_authenticator_removal')}</PopupSubtitle>

          <div className={styles.main}>
            <h5>
              {t('verification')} <span>1/2</span>
            </h5>

            <p>{t('enter_6digit_code')}<span> {user?.email}</span></p>

            <CodeVerificationInput
              code={code}
              setCode={setCode}
              isCodeValid={isCodeValid}
              setIsCodeValid={setIsCodeValid}
              errorMsg={errorCode}
              setErrorMsg={setErrorCode}
              isLoading={isLoading}
              countSeconds={TIMER_RESEND}
              onResendCode={resendEmailCode}
              verifyCode={verifyCode}
              resendBtn
              showError
              resendWithCaptcha
            />
          </div>

          <ButtonWhite className={styles.backBtn} onClick={() => setStep(2)}>{t('back')}</ButtonWhite>
        </>
      )}

      {step === 4 && (
        <>
          <PopupHeading className={styles['title-verification']}>{t('remove_authenticator')}</PopupHeading>
          <PopupSubtitle className={styles.subtitle}>{t('verify_auth_removal')}</PopupSubtitle>

          <div className={styles.main}>
            <h5>
              {t('verification')} <span>2/2</span>
            </h5>

            {removeFlow === ERemove2faFlow.AUTHENTICATOR
              ? <p dangerouslySetInnerHTML={{
                __html: t('enter_2fa_to_disable_auth')
              }}></p>
              : <p>{t('enter_6digit_code')}<span> {user?.fallback_email}</span></p>
            }

            <CodeVerificationInput
              code={code}
              setCode={setCode}
              isCodeValid={isCodeValid}
              setIsCodeValid={setIsCodeValid}
              errorMsg={errorCode}
              setErrorMsg={setErrorCode}
              isLoading={isLoading}
              countSeconds={TIMER_RESEND}
              verifyCode={verifyCode}
              onResendCode={resendBackupEmailCode}
              resendBtn={removeFlow === ERemove2faFlow.BACKUP}
              showError
              resendWithCaptcha={removeFlow === ERemove2faFlow.BACKUP}
            />
          </div>

          <ButtonWhite className={styles.backBtn} onClick={() => setStep(3)}>{t('back')}</ButtonWhite>
        </>
      )}

      {step === 5 && (
        <>
          <PopupHeading className={styles.title}>{t('authenticator_disabled')}</PopupHeading>

          <div className={styles.success}>
            <img src={SuccessIcon} alt="success icon" />
            <p>{t('you_disabled_google_auth')}</p>
          </div>
          <ButtonBlue className={styles.doneBtn} onClick={onClose}>{t('done')}</ButtonBlue>
        </>
      )}
    </PopupWrapper>,
    document.body,
  )
}
